import { useState, useRef, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";

type State = "idle" | "input" | "submitting" | "success";

const InlineWaitlistLink = () => {
  const [state, setState] = useState<State>("idle");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const revertTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (state === "input") {
      inputRef.current?.focus();
    }
  }, [state]);

  useEffect(() => {
    return () => {
      if (revertTimer.current) clearTimeout(revertTimer.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Invalid email");
      return;
    }

    setError("");
    setState("submitting");

    try {
      const { data, error: fnError } = await supabase.functions.invoke("join-waitlist", {
        body: { email: trimmed },
      });

      if (fnError) {
        let responseBody: any = null;
        try {
          const ctx = (fnError as any)?.context;
          if (ctx && typeof ctx.clone === "function") {
            responseBody = await (ctx as Response).clone().json();
          }
        } catch { /* ignore */ }

        if (responseBody?.status === "already_joined") {
          setState("success");
          revertTimer.current = setTimeout(() => setState("idle"), 3000);
          return;
        }
        throw new Error(responseBody?.error || fnError.message);
      }

      if (data?.status === "already_joined") {
        setState("success");
        revertTimer.current = setTimeout(() => setState("idle"), 3000);
        return;
      }

      trackEvent(AnalyticsEvents.WAITLIST_SIGNUP, { waitlist: true });
      setState("success");
      revertTimer.current = setTimeout(() => setState("idle"), 3000);
    } catch (err: any) {
      setError(err.message || "Something went wrong");
      setState("input");
    }
  };

  if (state === "idle") {
    return (
      <button
        onClick={() => setState("input")}
        className="text-foreground underline hover:no-underline font-medium"
      >
        Request an audit →
      </button>
    );
  }

  if (state === "success") {
    return (
      <span className="text-success font-medium animate-fade-in">
        You're on the list ✓
      </span>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="inline-flex items-center gap-1.5 animate-fade-in">
      <input
        ref={inputRef}
        type="email"
        placeholder="you@email.com"
        value={email}
        onChange={(e) => { setEmail(e.target.value); setError(""); }}
        className="h-7 w-40 px-2 text-xs rounded border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
        disabled={state === "submitting"}
      />
      <button
        type="submit"
        disabled={state === "submitting" || !email.trim()}
        className="h-7 px-2.5 text-xs font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {state === "submitting" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Go"}
      </button>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </form>
  );
};

export default InlineWaitlistLink;
